import { useEffect } from "react";

export default function HowToPlayPage() {
  useEffect(() => {
    document.title = "How to Play — ISPYFR";
  }, []);

  return (
    <div className="prose prose-sm max-w-none text-text">
      <h1 className="font-display text-2xl font-bold">How to Play ISPYFR</h1>
      <p className="text-lg text-text-secondary">
        ISPYFR is a real-world scavenger hunt game you play with friends.
        One person creates a game, shares an invite code, and everyone races to
        find and photograph items from the list. Here is everything you need to
        know to get started.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Creating a Game</h2>
      <p>
        Any player can create a new scavenger hunt in just a few steps. Tap the
        &ldquo;Create&rdquo; button from the home page, then choose a theme for your
        game. ISPYFR offers a selection of pre-built themed item lists — things
        like &ldquo;Neighborhood Walk,&rdquo; &ldquo;Park Adventure,&rdquo; or
        &ldquo;Holiday Hunt&rdquo; — so you can get started quickly without
        having to think of every item yourself. If you prefer, you can also
        build a completely custom list by typing in your own items. Choose
        anywhere from five to thirty items depending on how long you want the
        game to last.
      </p>
      <p>
        Once your list is ready, configure any optional rules. You can set a time
        limit so the game ends automatically, or leave it open-ended and end it
        manually whenever you are ready. You can also decide whether to moderate
        submissions yourself or assign trusted friends as moderators.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Inviting Players</h2>
      <p>
        After you create a game, you will receive a unique invite code — a short,
        easy-to-share code like <strong>HUNT-ABCD</strong>. Share this code with
        your friends however you like: text message, group chat, email, or even
        written on a whiteboard. Anyone who has the code can join.
      </p>
      <p>
        ISPYFR games are completely private and invitation-only. There is no
        public game browser or discovery feature. Only people who have your
        specific invite code can see or join your game, which means you always
        know exactly who is playing.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Joining a Game</h2>
      <p>
        To join a game, tap &ldquo;Join&rdquo; from the home page and enter the
        invite code you received. You will also pick a display name — this is how
        other players will see you during the game. No account or sign-up is
        required. Your display name only lasts for the duration of that one game
        session, so you can pick something fun and different every time.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Playing the Game</h2>
      <p>
        Once you have joined, you will see the full list of items you need to
        find. Your goal is to track down each item in the real world, take a photo
        of it, and submit it through the app. You can submit items in any order —
        work through the list strategically or just grab whatever you spot first.
      </p>
      <p>
        To submit a photo, tap on an item from the list, use your phone&apos;s camera
        to snap a picture, and hit submit. The photo is automatically compressed
        and uploaded. You will see a confirmation once your submission is
        received and waiting for review.
      </p>
      <p>
        Keep an eye on the game feed to see how other players are doing. You can
        track who has found which items and see how the competition is shaping up
        in real time.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Moderation</h2>
      <p>
        Every submission goes through a quick review step. The game creator — and
        any players they have designated as moderators — will see incoming
        submissions and can approve or reject each one. This keeps the game fair
        and ensures that photos actually match the item they claim to represent.
      </p>
      <p>
        If your submission is rejected, do not worry — you can try again. Take a
        clearer photo or find a better example of the item and resubmit. The
        moderator may leave a note explaining why it was rejected so you know what
        to look for.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Winning and Results</h2>
      <p>
        The game ends either when the time limit expires or when the creator
        manually ends it. At that point, final scores are tallied based on how
        many items each player successfully found and had approved. The results
        screen shows the final standings, a recap of all the photos submitted,
        and highlights from the game.
      </p>
      <p>
        After the game ends, you can still view the results and photos for a
        short window. Use this time to relive the highlights, share a recap
        collage with friends, or just enjoy the memories before the data is
        cleaned up.
      </p>

      <h2 className="mt-6 text-lg font-semibold">Tips for a Great Game</h2>
      <ul className="list-disc space-y-1 pl-5">
        <li>
          <strong>Mix easy and hard items.</strong> Include a few items that are
          obvious and nearby alongside some that require real effort to find. This
          keeps things exciting for everyone.
        </li>
        <li>
          <strong>Set a reasonable time limit.</strong> Thirty minutes to an hour
          works great for most groups. Too short and it feels rushed; too long and
          people lose interest.
        </li>
        <li>
          <strong>Play in an interesting location.</strong> Parks, downtown areas,
          shopping districts, and nature trails all make for fantastic scavenger
          hunt settings.
        </li>
        <li>
          <strong>Assign a dedicated moderator.</strong> If the game creator also
          wants to play, assign a friend as moderator so submissions get reviewed
          quickly.
        </li>
        <li>
          <strong>Be creative with your list.</strong> Instead of generic items,
          try specific challenges like &ldquo;something that makes you laugh&rdquo;
          or &ldquo;the oldest thing you can find.&rdquo;
        </li>
      </ul>

      <h2 className="mt-6 text-lg font-semibold">Privacy and Cleanup</h2>
      <p>
        ISPYFR is designed with privacy at its core. No accounts are required, no
        personal information is collected beyond a temporary display name, and all
        photos and game data are automatically deleted after the game&apos;s retention
        window ends. Nothing is stored long-term. You can play with confidence
        knowing your data will not linger on our servers.
      </p>
    </div>
  );
}
